import { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { useNavigate, Link } from 'react-router-dom';
import { useFastAuth, UserIntent, AccountAttributes } from '@/hooks/useFastAuth';
import { LemonadeAvatar } from './LemonadeAvatar';
import { LemonadeProgress } from './LemonadeProgress';
import { CountdownTimer } from './CountdownTimer';
import { RubyPublicFAB } from '@/components/shared/RubyPublicFAB';
import { LemonadeProfileWizard } from '@/components/dashboard/profile/LemonadeProfileWizard';
import { useProfileWizard } from '@/contexts/ProfileWizardContext';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Check, ChevronLeft, Shield, AlertTriangle, User, Users, Trophy, ArrowRight, TrendingUp, Database, Building, Ticket, RefreshCw, MapPin, Zap, Clock, Sparkles, Gift, Wallet, ShieldAlert } from 'lucide-react';


interface LemonadeOnboardingProps {
  referralCode?: string | null;
  initialEmail?: string;
  source?: string | null;
  onSwitchToLogin: () => void;
  onSignupStarted?: () => void;
  onSignupComplete?: () => void;
  onForgotPassword?: () => void;
}

type OnboardingStep = 'welcome' | 'products' | 'email' | 'loading' | 'reveal' | 'magic-link' | 'creating' | 'complete' | 'profile-unlock';

const PRODUCTS = [
  {
    id: 'rewards' as UserIntent,
    icon: (
      <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="20" cy="20" r="16" />
        <path d="M20 12v16M14 18l6-6 6 6" />
      </svg>
    ),
    title: 'Get paid for your data',
    description: 'Share what you want. Earn cash and entries.',
    badge: 'Avg $50/year',
    badgeColor: 'bg-green-100 text-green-700',
  },
  {
    id: 'jackpot' as UserIntent,
    icon: (
      <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 30h16M15 30V18c0-2.8 2.2-5 5-5s5 2.2 5 5v12" />
        <circle cx="20" cy="11" r="3" />
      </svg>
    ),
    title: 'Win weekly cash prizes',
    description: '$1,000 drawn every week. $5K monthly.',
    badge: '$1K weekly',
    badgeColor: 'bg-primary/20 text-primary-foreground',
  },
  {
    id: 'exposure' as UserIntent,
    icon: (
      <svg viewBox="0 0 40 40" className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 4l16 8v10c0 9-7 14-16 18-9-4-16-9-16-18V12l16-8z" />
        <path d="M14 20l4 4 8-8" />
      </svg>
    ),
    title: "See what's exposed",
    description: 'Breaches, data brokers, dark web scans.',
    badge: 'Free scan',
    badgeColor: 'bg-blue-100 text-blue-700',
  },
];

export function LemonadeOnboarding({ referralCode, initialEmail, source, onSwitchToLogin, onSignupStarted, onSignupComplete, onForgotPassword }: LemonadeOnboardingProps) {
  const navigate = useNavigate();
  
  // Profile wizard context for post-signup flow
  const { 
    remainingEntries, 
    openWizard, 
    isOpen: wizardOpen,
    closeWizard 
  } = useProfileWizard();
  
  const [showProfileWizard, setShowProfileWizard] = useState(false);
  
  // If we have an initial email, skip to loading step
  const getInitialStep = (): OnboardingStep => {
    if (initialEmail) return 'loading';
    return 'welcome';
  };
  
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(getInitialStep);
  const [hasAccount, setHasAccount] = useState<boolean | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<UserIntent[]>(
    initialEmail ? ['rewards', 'jackpot', 'exposure'] : []
  );

  const attribution = (() => {
    try {
      const stored = localStorage.getItem('reklaim_attribution');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  })();

  const {
    step: authStep,
    email,
    lookupData,
    entriesAwarded,
    isLoading,
    error,
    resendCooldown,
    setIntent,
    submitEmail,
    sendOtp,
    verifyOtp,
  } = useFastAuth({
    referralCode,
    initialEmail,
    source: attribution.source,
    medium: attribution.medium,
    campaign: attribution.campaign,
    redirectTo: source === 'protect' ? '/dashboard/protect' : '/dashboard',
  });

  const [emailInput, setEmailInput] = useState(initialEmail || '');
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');
  // Auto-trigger email scan when we have initialEmail
  useEffect(() => {
    if (initialEmail && currentStep === 'loading' && !lookupData && !isLoading) {
      submitEmail(initialEmail, ['rewards', 'jackpot', 'exposure']);
    }
  }, [initialEmail, currentStep, lookupData, isLoading, submitEmail]);

  const handleWelcomeSelect = (value: boolean) => {
    setHasAccount(value);
    if (value) {
      onSwitchToLogin();
    } else {
      setCurrentStep('products');
    }
  };

  const handleProductToggle = (id: UserIntent) => {
    setSelectedProducts(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handleProductsNext = () => {
    if (selectedProducts.length > 0) {
      setIntent(selectedProducts);
      setCurrentStep('email');
    }
  };

  const handleEmailSubmit = async () => {
    if (emailInput.trim()) {
      await submitEmail(emailInput.trim());
      setCurrentStep('loading');
    }
  };

  // Sync with auth step
  useEffect(() => {
    if (authStep === 'reveal' && currentStep === 'loading') {
      setCurrentStep('reveal');
    }
    // If lookup failed (authStep goes to 'email') and we're stuck on loading, unblock
    if (authStep === 'email' && currentStep === 'loading') {
      // Drop back to email entry so user can retry
      setCurrentStep('email');
    }
    // Handle magic-link step - allow returning from 'creating' if there's an error
    if (authStep === 'magic-link') {
      if (currentStep === 'creating' && error) {
        // Error during account creation - go back to magic-link step
        setCurrentStep('magic-link');
      } else if (currentStep !== 'magic-link' && currentStep !== 'creating' && currentStep !== 'complete' && currentStep !== 'profile-unlock') {
        setCurrentStep('magic-link');
        // Notify parent that signup is starting - prevents auto-redirect
        onSignupStarted?.();
      }
    }
    if (authStep === 'creating' && currentStep !== 'creating' && !error) {
      setCurrentStep('creating');
    }
    if (authStep === 'complete' && currentStep !== 'complete' && currentStep !== 'profile-unlock') {
      setCurrentStep('complete');
      localStorage.removeItem('reklaim_email');
      // Show profile unlock step after brief celebration
      setTimeout(() => setCurrentStep('profile-unlock'), 1200);
    }
  }, [authStep, currentStep, error, onSignupStarted]);

  const canGoBack = !initialEmail && ['products', 'email'].includes(currentStep);

  const handleBack = () => {
    if (currentStep === 'products') setCurrentStep('welcome');
    if (currentStep === 'email') setCurrentStep('products');
  };

  const getProgressStep = () => {
    if (['welcome', 'products'].includes(currentStep)) return 0;
    if (['email', 'loading', 'reveal'].includes(currentStep)) return 1;
    return 2;
  };

  return (
    <div className="flex-1 flex">
      {/* Left Sidebar - Progress (desktop only) */}
      <div className="hidden lg:flex w-64 flex-shrink-0 pl-16 pt-20">
        <LemonadeProgress 
          currentStep={getProgressStep()} 
          steps={initialEmail ? ['Your data', 'Your Results'] : ['About you', 'Your data', 'Your Results']} 
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-start pt-6 lg:pt-12 px-4 relative">
        {/* Back Button */}
        {canGoBack && (
          <button
            onClick={handleBack}
            className="absolute left-4 top-6 lg:left-8 lg:top-12 p-1.5 hover:bg-muted rounded-sm transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </button>
        )}

        {/* Avatar - hidden on reveal step to save space */}
        {currentStep !== 'reveal' && <LemonadeAvatar />}

        {/* Content based on step */}
        <div className={`w-full mt-8 ${currentStep === 'reveal' ? 'max-w-6xl' : 'max-w-lg'}`}>
          {currentStep === 'welcome' && (
            <WelcomeStep onSelect={handleWelcomeSelect} />
          )}

          {currentStep === 'products' && (
            <ProductsStep
              products={PRODUCTS}
              selected={selectedProducts}
              onToggle={handleProductToggle}
              onNext={handleProductsNext}
              canProceed={selectedProducts.length > 0}
            />
          )}

          {currentStep === 'email' && (
            <EmailStep
              firstName={firstNameInput}
              lastName={lastNameInput}
              email={emailInput}
              onFirstNameChange={setFirstNameInput}
              onLastNameChange={setLastNameInput}
              onEmailChange={setEmailInput}
              onSubmit={handleEmailSubmit}
              isLoading={isLoading}
              error={error}
            />
          )}

          {currentStep === 'loading' && <LoadingStep />}

          {currentStep === 'reveal' && lookupData && (
            <RevealStep
              email={email || initialEmail || ''}
              breachData={lookupData.breachData}
              profileData={lookupData.profileData}
              entriesAwarded={entriesAwarded}
              calculation={lookupData.calculation}
              brandCount={lookupData.brandCount ?? 7}
              dataBrokerCount={lookupData.dataBrokerCount ?? 5}
              accountAttributes={lookupData.accountAttributes}
              hasUnveilData={lookupData.hasUnveilData}
              isProtectSource={source === 'protect'}
              onClaim={sendOtp}
            />
          )}

          {currentStep === 'magic-link' && (
            <MagicLinkStep
              email={email || initialEmail || ''}
              onResend={sendOtp}
              onVerifyOtp={verifyOtp}
              isLoading={isLoading}
              error={error}
              resendCooldown={resendCooldown}
              source={source}
            />
          )}

          {currentStep === 'creating' && <CreatingStep />}

          {currentStep === 'complete' && lookupData && (
            <CompleteStep 
              earnEntries={lookupData.calculation.base + lookupData.calculation.profileBonus} 
            />
          )}
          {currentStep === 'complete' && !lookupData && (
            <CompleteStep earnEntries={entriesAwarded} />
          )}
          
          {currentStep === 'profile-unlock' && (
            <ProfileUnlockStep 
              remainingEntries={remainingEntries}
              onStartWizard={() => setShowProfileWizard(true)}
              onSkip={() => {
                onSignupComplete?.();
              }}
            />
          )}
        </div>

        {/* Ruby FAB - context-aware helper */}
        <RubyPublicFAB
          context={{
            type: 'auth',
            signupStep: currentStep,
            email: email || initialEmail,
            breachCount: lookupData?.breachData?.length,
            entriesEarned: lookupData 
              ? lookupData.calculation.base + lookupData.calculation.profileBonus 
              : entriesAwarded,
          }}
        />
      </div>
      
      {/* Profile Wizard Overlay */}
      {showProfileWizard && (
        <LemonadeProfileWizard 
          isOpen={showProfileWizard} 
          onClose={() => {
            setShowProfileWizard(false);
            onSignupComplete?.();
          }} 
        />
      )}
    </div>
  );
}

// Sub-components

function WelcomeStep({ onSelect }: { onSelect: (hasAccount: boolean) => void }) {
  const [selected, setSelected] = useState<boolean | null>(null);

  return (
    <div className="text-left space-y-8">
      <div className="space-y-2">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Create your account
        </h1>
        <p className="text-lg text-muted-foreground">
          Do you already have a Reklaim account?
        </p>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => setSelected(true)}
          className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-colors ${
            selected === true
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-muted-foreground bg-background'
          }`}
        >
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
            selected === true ? 'border-primary bg-primary' : 'border-muted-foreground'
          }`}>
            {selected === true && <div className="w-2 h-2 bg-primary-foreground rounded-full" />}
          </div>
          <span className="text-lg font-medium text-foreground">Yes, I have an account</span>
        </button>

        <button
          onClick={() => setSelected(false)}
          className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-colors ${
            selected === false
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-muted-foreground bg-background'
          }`}
        >
          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
            selected === false ? 'border-primary bg-primary' : 'border-muted-foreground'
          }`}>
            {selected === false && <div className="w-2 h-2 bg-primary-foreground rounded-full" />}
          </div>
          <span className="text-lg font-medium text-foreground">No, I'm new here</span>
        </button>
      </div>

      <button
        onClick={() => selected !== null && onSelect(selected)}
        disabled={selected === null}
        className={`w-full py-4 rounded-lg font-medium text-lg transition-colors ${
          selected !== null
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground cursor-not-allowed'
        }`}
      >
        Continue
      </button>
    </div>
  );
}

function ProductsStep({
  products,
  selected,
  onToggle,
  onNext,
  canProceed,
}: {
  products: typeof PRODUCTS;
  selected: UserIntent[];
  onToggle: (id: UserIntent) => void;
  onNext: () => void;
  canProceed: boolean;
}) {
  const allSelected = products.every(p => selected.includes(p.id));

  const handleSelectAll = () => {
    if (allSelected) {
      products.forEach(p => {
        if (selected.includes(p.id)) onToggle(p.id);
      });
    } else {
      products.forEach(p => {
        if (!selected.includes(p.id)) onToggle(p.id);
      });
    }
  };

  return (
    <div className="text-left space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Select your interests
        </h1>
        <p className="text-lg text-muted-foreground">
          Choose what you'd like to do with Reklaim
        </p>
      </div>

      {/* Select All */}
      <button
        onClick={handleSelectAll}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors text-sm font-medium"
      >
        <Check className="w-4 h-4" />
        {allSelected ? 'Deselect all' : 'Select all'}
      </button>

      {/* Product Cards */}
      <div className="space-y-3">
        {products.map((product) => {
          const isSelected = selected.includes(product.id);
          return (
            <button
              key={product.id}
              onClick={() => onToggle(product.id)}
              className={`w-full flex items-start gap-4 p-4 rounded-lg border-2 transition-colors text-left ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground bg-background'
              }`}
            >
              <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                isSelected ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {product.icon}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-medium text-foreground">{product.title}</span>
                  <span className={`text-sm font-medium px-2 py-0.5 rounded-md ${product.badgeColor}`}>
                    {product.badge}
                  </span>
                </div>
                <p className="text-base text-muted-foreground mt-1">{product.description}</p>
              </div>

              <div className={`flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
              }`}>
                {isSelected && <Check className="w-4 h-4 text-primary-foreground" />}
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={onNext}
        disabled={!canProceed}
        className={`w-full py-4 rounded-lg font-medium text-lg transition-colors ${
          canProceed
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground cursor-not-allowed'
        }`}
      >
        Continue
      </button>
    </div>
  );
}

function EmailStep({
  firstName,
  lastName,
  email,
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onSubmit,
  isLoading,
  error,
}: {
  firstName: string;
  lastName: string;
  email: string;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  error: string | null;
}) {
  const isValid = email.includes('@');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid && !isLoading) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="text-left space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Enter your details
        </h1>
        <p className="text-lg text-muted-foreground">
          We'll use this to create your account
        </p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="First name"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            className="px-4 py-4 rounded-lg border-2 border-border focus:border-primary focus:outline-none text-lg text-foreground placeholder-muted-foreground bg-background"
          />
          <input
            type="text"
            placeholder="Last name"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            className="px-4 py-4 rounded-lg border-2 border-border focus:border-primary focus:outline-none text-lg text-foreground placeholder-muted-foreground bg-background"
          />
        </div>

        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          className="w-full px-4 py-4 rounded-lg border-2 border-border focus:border-primary focus:outline-none text-lg text-foreground placeholder-muted-foreground bg-background"
          autoFocus
        />

        {error && (
          <p className="text-base text-destructive">{error}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={!isValid || isLoading}
        className={`w-full py-4 rounded-lg font-medium text-lg transition-colors flex items-center justify-center gap-2 ${
          isValid && !isLoading
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground cursor-not-allowed'
        }`}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Checking...
          </>
        ) : (
          'Continue'
        )}
      </button>

      {/* Exit option */}
      <p className="text-center text-sm text-muted-foreground mt-4">
        Not ready?{' '}
        <Link to="/" className="text-primary hover:underline">
          Come back later
        </Link>
      </p>
    </form>
  );
}

function LoadingStep() {
  return (
    <div className="text-center space-y-6 py-12">
      <div className="relative w-20 h-20 mx-auto">
        <div className="absolute inset-0 rounded-full border-2 border-muted" />
        <div className="absolute inset-0 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
      <p className="text-lg text-muted-foreground">Scanning for your data...</p>
    </div>
  );
}

function RevealStep({
  email,
  breachData,
  profileData,
  entriesAwarded,
  calculation,
  brandCount,
  dataBrokerCount,
  accountAttributes,
  hasUnveilData,
  isProtectSource,
  onClaim,
}: {
  email: string;
  breachData: { name: string; severity: string; dataTypes: string[] }[];
  profileData: { key: string; value: string }[];
  entriesAwarded: number;
  calculation: {
    base: number;
    breachBonus: number;
    profileBonus: number;
    criticalBonus: number;
  };
  brandCount: number;
  dataBrokerCount: number;
  accountAttributes: AccountAttributes | null;
  hasUnveilData: boolean;
  isProtectSource?: boolean;
  onClaim: () => void;
}) {
  const [ageTermsConfirmed, setAgeTermsConfirmed] = useState(false);
  const hasBreaches = breachData.length > 0;
  const hasProfile = profileData.length > 0;

  // Calculate entries WITHOUT breach bonus (only base + profile)
  const earnEntries = calculation.base + calculation.profileBonus;

  // Animated counter — declared before the protect-first early return so hook
  // order stays stable across renders.
  const [displayCount, setDisplayCount] = useState(0);

  const fireCelebrationConfetti = useCallback(() => {
    const duration = 2000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ['#fbbf24', '#f59e0b', '#d97706', '#ffffff'],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ['#fbbf24', '#f59e0b', '#d97706', '#ffffff'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, []);

  useEffect(() => {
    // The protect-first layout has no animated counter.
    if (isProtectSource) return;

    const duration = 1200;
    const steps = 30;
    const stepValue = earnEntries / steps;
    const stepDuration = duration / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += stepValue;
      if (current >= earnEntries) {
        setDisplayCount(earnEntries);
        clearInterval(timer);
        // Fire confetti when counter finishes
        fireCelebrationConfetti();
      } else {
        setDisplayCount(Math.floor(current));
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [earnEntries, fireCelebrationConfetti, isProtectSource]);

  // ─── Protect-first layout (source=protect) ───────────────────────────────
  if (isProtectSource) {
    const visibleBreaches = breachData.slice(0, 2);
    const hiddenCount = Math.max(0, breachData.length - 2);
    const brokerDisplay = dataBrokerCount > 0 ? dataBrokerCount : (hasBreaches ? Math.min(breachData.length * 4, 32) : 12);

    const ProtectCTA = () => (
      <div className="space-y-3">
        <button
          onClick={() => {
            if (ageTermsConfirmed) {
              localStorage.setItem('reklaim_terms_accepted_at', new Date().toISOString());
              onClaim();
            }
          }}
          disabled={!ageTermsConfirmed}
          className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 shadow-lg ${
            ageTermsConfirmed
              ? 'bg-[hsl(var(--protect-accent))] text-white hover:opacity-90 hover:shadow-xl hover:-translate-y-0.5'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          <Shield className="w-5 h-5" />
          {hasBreaches ? 'Remove My Data — Free' : 'Protect My Data — Free'}
          <ArrowRight className="w-5 h-5" />
        </button>
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={ageTermsConfirmed}
            onChange={(e) => setAgeTermsConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border border-primary accent-primary"
          />
          <span className="text-sm text-muted-foreground leading-snug">
            I confirm that I am 18 years of age or older and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms and Conditions</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>.
          </span>
        </label>
        <p className="text-center text-xs text-muted-foreground">Free forever · No credit card · Cancel anytime</p>
      </div>
    );

    return (
      <div className="text-left space-y-6 pb-28 lg:pb-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(var(--protect-accent))]/10 border border-[hsl(var(--protect-accent))]/30 mb-1">
            <Shield className="w-4 h-4 text-[hsl(var(--protect-accent))]" />
            <span className="text-sm font-semibold text-[hsl(var(--protect-accent))]">PROTECT by REKLAIM</span>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-foreground">
            {hasBreaches ? 'Your data has been exposed.' : 'Your data is at risk.'}
          </h1>
          <p className="text-base text-muted-foreground">
            <span className="font-medium text-foreground">{email}</span> · Create a free account to start removing it.
          </p>
        </div>

        {/* Hero breach + broker stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className={`rounded-2xl p-4 border ${hasBreaches ? 'bg-red-500/10 border-red-500/30' : 'bg-orange-500/10 border-orange-500/30'}`}>
            <div className={`text-3xl font-bold ${hasBreaches ? 'text-red-500' : 'text-orange-500'}`}>
              {hasBreaches ? breachData.length : '0'}
            </div>
            <div className="text-sm font-medium text-foreground mt-0.5">
              {hasBreaches ? 'Data breaches' : 'Breaches found'}
            </div>
            <div className="text-xs text-muted-foreground">{hasBreaches ? 'Your email was leaked' : 'Stay protected'}</div>
          </div>
          <div className="rounded-2xl p-4 bg-[hsl(var(--protect-accent))]/10 border border-[hsl(var(--protect-accent))]/30">
            <div className="text-3xl font-bold text-[hsl(var(--protect-accent))]">{brokerDisplay}+</div>
            <div className="text-sm font-medium text-foreground mt-0.5">Data brokers</div>
            <div className="text-xs text-muted-foreground">Selling your profile online</div>
          </div>
        </div>

        {/* Exposed profile card */}
        {hasUnveilData && accountAttributes && (
          <div className="rounded-2xl border border-[hsl(var(--protect-accent))]/30 bg-[hsl(var(--protect-accent))]/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-[hsl(var(--protect-accent))]" />
              <p className="text-sm font-semibold text-foreground">Your Exposed Profile</p>
            </div>
            <div className="space-y-1.5 text-sm">
              {accountAttributes.first_name && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-16 shrink-0">Name:</span>
                  <span className="font-medium text-foreground">{accountAttributes.first_name[0]}*** {accountAttributes.last_name?.[0] ?? ''}***</span>
                </div>
              )}
              {accountAttributes.addresses?.[0] && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-16 shrink-0">Location:</span>
                  <span className="font-medium text-foreground">{accountAttributes.addresses[0].city}, {accountAttributes.addresses[0].state}</span>
                </div>
              )}
              {accountAttributes.age && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-16 shrink-0">Age:</span>
                  <span className="font-medium text-foreground">{accountAttributes.age}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Breach list with blur-lock */}
        {hasBreaches && (
          <div className="rounded-2xl border border-border/50 bg-card/50 overflow-hidden">
            <div className="px-4 py-3 border-b border-border/50 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <span className="text-sm font-semibold text-foreground">Breaches Detected</span>
            </div>
            <div className="divide-y divide-border/50">
              {visibleBreaches.map((breach, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-foreground font-medium truncate">{breach.name}</span>
                  <span className={`ml-2 shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                    breach.severity === 'critical' ? 'bg-red-500/20 text-red-600' :
                    breach.severity === 'high' ? 'bg-orange-500/20 text-orange-600' :
                    'bg-yellow-500/20 text-yellow-600'
                  }`}>{breach.severity}</span>
                </div>
              ))}
              {hiddenCount > 0 && (
                <div className="flex items-center justify-between px-4 py-2.5 bg-muted/40">
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4 text-[hsl(var(--protect-accent))]" />
                    <span className="font-medium text-[hsl(var(--protect-accent))]">+{hiddenCount} more hidden</span>
                    <span className="text-muted-foreground">— create account to see all</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Plan comparison */}
        <div className="rounded-2xl border border-[hsl(var(--protect-accent))]/30 bg-[hsl(var(--protect-accent))]/5 p-4 space-y-3">
          <p className="text-sm font-semibold text-foreground">What happens when you sign up:</p>
          <div className="space-y-2">
            {[
              { icon: <Shield className="w-4 h-4" />, text: '1 free data broker removal — no card needed' },
              { icon: <AlertTriangle className="w-4 h-4" />, text: 'Full breach report for your email' },
              { icon: <Database className="w-4 h-4" />, text: 'See exactly which brokers have your data' },
              { icon: <Zap className="w-4 h-4" />, text: 'Upgrade to Protect Plus for continuous removal' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm">
                <div className="mt-0.5 text-[hsl(var(--protect-accent))]">{item.icon}</div>
                <span className="text-foreground">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop CTA */}
        <div className="hidden lg:block">
          <ProtectCTA />
        </div>

        {/* Mobile sticky CTA */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-background/90 backdrop-blur-lg border-t border-border safe-area-inset-bottom z-50">
          <div className="flex items-center justify-center gap-2 mb-3 text-xs text-muted-foreground">
            <Shield className="w-4 h-4 text-[hsl(var(--protect-accent))]" />
            <span className="font-medium text-foreground">Free data protection · Upgrade anytime</span>
          </div>
          <ProtectCTA />
        </div>
      </div>
    );
  }
  // ─── End protect-first layout ─────────────────────────────────────────────

  return (
    <div className="text-left space-y-8 pb-24 lg:pb-8">
      {/* Header Section */}
      <div className="text-center lg:text-left space-y-3">
        <div className="opacity-0 animate-fade-up flex items-center justify-center lg:justify-start gap-2 flex-wrap">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-muted text-muted-foreground text-sm font-medium">
            Your Results
          </span>
        </div>
        <div className="space-y-2 opacity-0 animate-fade-up animate-stagger-1">
          <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
            We Found You!
          </h1>
          <p className="text-base text-muted-foreground">
            <span className="font-medium text-foreground">{email}</span> · Sign up to claim your entries
          </p>
        </div>
      </div>

      {/* Main 50/50 Split Layout */}
      <div className="opacity-0 animate-fade-up animate-stagger-2 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: EARN Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-primary uppercase tracking-wide flex items-center gap-2">
            <Ticket className="w-4 h-4" />
            Earn
          </h3>
          
          {/* Yellow Entry Box */}
          <div className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground rounded-2xl shadow-xl">
            <div className="shimmer-overlay pointer-events-none rounded-2xl" />
            <div className="relative z-10 p-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-foreground/20 backdrop-blur-sm flex items-center justify-center">
                    <Trophy className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-5xl lg:text-6xl font-bold tracking-tight animate-count-up-glow">
                      {displayCount}
                    </p>
                    <p className="text-sm font-medium opacity-90">
                      entries in <span className="font-bold">$1K</span> weekly draw
                    </p>
                  </div>
                </div>
                <div className="hidden sm:block bg-primary-foreground/15 rounded-lg p-3 border border-primary-foreground/20">
                  <p className="text-xs opacity-80 mb-1">Next draw:</p>
                  <CountdownTimer />
                </div>
              </div>
            </div>
          </div>
          
          {/* Green Brands Box with points earned message */}
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/30 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Ticket className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="font-bold text-foreground text-lg">{brandCount}+ Brands</p>
                <p className="text-sm text-muted-foreground">Want access to your insights</p>
              </div>
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <Check className="w-3 h-3 text-green-500" />
                <span>Quick surveys = more entries</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-3 h-3 text-green-500" />
                <span>Earn points for every answer</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-3 h-3 text-green-500" />
                <span>Weekly $1K + monthly jackpots</span>
              </div>
            </div>
            
            {/* Points Earned Message - REK-585 */}
            <div className="mt-3 pt-3 border-t border-green-500/20">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm font-medium text-green-600 dark:text-green-400">
                  You've earned 10 points already by scanning your email. Complete the sign up and earn 25 more points! And then complete your Profile to earn up to 190 additional points!
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: PROTECT Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-red-500 uppercase tracking-wide flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" />
            Protect
          </h3>
          
          {/* Red Breach Box with data broker count */}
          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl border border-orange-500/30 p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-orange-500" />
              </div>
              <div>
                <p className="font-bold text-foreground text-lg">
                  {hasBreaches ? `${breachData.length} Breaches` : 'No Breaches'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {hasBreaches ? 'Your data was exposed' : 'Great news!'}
                </p>
              </div>
            </div>
            
            {/* Data Broker Count - REK-584, REK-641 */}
            {(hasBreaches || dataBrokerCount > 0) && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <Database className="w-4 h-4 text-red-500" />
                <span className="text-base font-medium text-red-600 dark:text-red-400">
                  {dataBrokerCount > 0 ? dataBrokerCount : `~${Math.min(breachData.length * 3, 25)}`} data brokers may have your info
                </span>
              </div>
            )}
          </div>
          
          {/* Real Unveil Data Display - Your Exposed Profile */}
          {hasUnveilData && accountAttributes && (
            <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 rounded-2xl border border-purple-500/30 p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <User className="w-4 h-4 text-purple-500" />
                </div>
                <div>
                  <p className="font-bold text-foreground text-sm">Your Exposed Profile</p>
                  <p className="text-xs text-muted-foreground">Data found about you online</p>
                </div>
              </div>
              
              <div className="space-y-2">
                {accountAttributes.first_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Name:</span>
                    <span className="font-medium text-foreground">
                      {accountAttributes.first_name[0]}*** {accountAttributes.last_name?.[0] ?? ''}***
                    </span>
                  </div>
                )}
                
                {accountAttributes.addresses?.[0] && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">Location:</span>
                    <span className="font-medium text-foreground">
                      {accountAttributes.addresses[0].city}, {accountAttributes.addresses[0].state}
                    </span>
                  </div>
                )}
                
                {accountAttributes.age && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Age:</span>
                    <span className="font-medium text-foreground">{accountAttributes.age}</span>
                  </div>
                )}
                
                {accountAttributes.gender && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Gender:</span>
                    <span className="font-medium text-foreground">{accountAttributes.gender}</span>
                  </div>
                )}
                
                {accountAttributes.marital_status && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Status:</span>
                    <span className="font-medium text-foreground">{accountAttributes.marital_status}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Breach list - scrollable */}
          {hasBreaches && (
            <div className="max-h-48 overflow-y-auto space-y-2 rounded-xl border border-border/50 p-3 bg-card/50">
              {breachData.map((breach, i) => (
                <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg bg-muted/50">
                  <span className="text-foreground truncate">{breach.name}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    breach.severity === 'critical' ? 'bg-red-500/20 text-red-600' :
                    breach.severity === 'high' ? 'bg-orange-500/20 text-orange-600' :
                    'bg-yellow-500/20 text-yellow-600'
                  }`}>
                    {breach.severity}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Desktop CTA Button */}
      <div className="hidden lg:block opacity-0 animate-fade-up animate-stagger-4">
        <button
          onClick={() => {
            if (ageTermsConfirmed) {
              localStorage.setItem('reklaim_terms_accepted_at', new Date().toISOString());
              onClaim();
            }
          }}
          disabled={!ageTermsConfirmed}
          className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 shadow-lg ${
            ageTermsConfirmed
              ? 'bg-foreground text-background hover:bg-foreground/90 hover:shadow-xl hover:-translate-y-0.5'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          Sign Up Now
          <ArrowRight className="w-5 h-5" />
        </button>

        {/* Age & Terms Checkbox */}
        <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={ageTermsConfirmed}
            onChange={(e) => setAgeTermsConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border border-primary accent-primary"
          />
          <span className="text-sm text-muted-foreground leading-snug">
            I confirm that I am 18 years of age or older and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms and Conditions</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>.
          </span>
        </label>

        <p className="text-center text-sm text-muted-foreground mt-2">
          Sign up in 30 seconds · No cost
        </p>
        
        {/* Gold Jackpot Box - REK-586 */}
        <div className="mt-4 relative overflow-hidden bg-gradient-to-br from-amber-500/20 to-yellow-500/10 rounded-xl border border-amber-500/30 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Weekly $1K Jackpot</p>
              <p className="text-xs text-muted-foreground">Your entries are automatically included</p>
            </div>
          </div>
        </div>
      </div>


      {/* Mobile Sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-lg border-t border-border safe-area-inset-bottom z-50">
        {/* Gold Jackpot Mini Badge - Mobile */}
        <div className="flex items-center justify-center gap-2 mb-3 text-sm">
          <Trophy className="w-4 h-4 text-amber-500" />
          <span className="font-medium text-foreground">$1K Weekly Jackpot</span>
        </div>

        {/* Age & Terms Checkbox - Mobile */}
        <label className="flex items-start gap-3 mb-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={ageTermsConfirmed}
            onChange={(e) => setAgeTermsConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border border-primary accent-primary"
          />
          <span className="text-xs text-muted-foreground leading-snug">
            I confirm that I am 18 years of age or older and agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Terms and Conditions</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Privacy Policy</a>.
          </span>
        </label>
        
        <button
          onClick={() => {
            if (ageTermsConfirmed) {
              localStorage.setItem('reklaim_terms_accepted_at', new Date().toISOString());
              onClaim();
            }
          }}
          disabled={!ageTermsConfirmed}
          className={`w-full py-4 rounded-2xl font-bold text-lg transition-all flex items-center justify-center gap-3 shadow-xl ${
            ageTermsConfirmed
              ? 'bg-foreground text-background hover:bg-foreground/90 animate-bounce-subtle'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          Sign Up Now
          <ArrowRight className="w-5 h-5" />
        </button>
        <p className="text-center text-xs text-muted-foreground mt-2">
          Sign up · No cost · 30 seconds
        </p>
      </div>
    </div>
  );
}

function MagicLinkStep({
  email,
  onResend,
  onVerifyOtp,
  isLoading,
  error,
  resendCooldown,
  source,
}: {
  email: string;
  onResend: () => void;
  onVerifyOtp: (code: string) => void;
  isLoading: boolean;
  error: string | null;
  resendCooldown: number;
  source?: string | null;
}) {
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);

  const postAuthPath = source === 'protect' ? '/dashboard/protect' : '/dashboard';

  // Listen for auth changes (user clicked magic link in another tab)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        navigate(postAuthPath);
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate, postAuthPath]);

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length === 6 && acceptedTerms) {
      onVerifyOtp(otpCode);
    }
  };

  return (
    <div className="text-left space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Check your email
        </h1>
        <p className="text-lg text-muted-foreground">
          We sent a sign-in link to{' '}
          <span className="font-medium text-foreground">{email}</span>
        </p>
      </div>
      
      {/* Email icon */}
      <div className="flex justify-center py-4">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Zap className="w-8 h-8 text-primary" />
        </div>
      </div>
      
      {!showOtpInput ? (
        <>
          <p className="text-center text-muted-foreground">
            Click the link in your email to complete signup
          </p>
          
          {/* Option to enter code manually */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowOtpInput(true)}
              className="text-sm text-primary hover:underline"
            >
              Or enter the 6-digit code
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={handleOtpSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Enter the 6-digit code from your email
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full px-4 py-4 rounded-lg border-2 border-border focus:border-primary focus:outline-none text-2xl text-center tracking-[0.5em] font-mono text-foreground placeholder-muted-foreground bg-background"
              autoFocus
            />
          </div>
          
          <button
            type="submit"
            disabled={otpCode.length !== 6 || !acceptedTerms || isLoading}
            className="w-full py-4 rounded-lg font-medium text-lg bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </button>
          
          <button
            type="button"
            onClick={() => setShowOtpInput(false)}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Back to magic link
          </button>
        </form>
      )}
      
      {/* Resend option */}
      <div className="text-center">
        <span className="text-sm text-muted-foreground">Didn't receive it? </span>
        <button
          type="button"
          onClick={onResend}
          disabled={resendCooldown > 0 || isLoading}
          className="text-sm text-primary hover:underline disabled:opacity-50"
        >
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend link'}
        </button>
      </div>
      
      {/* Terms of Service Checkbox */}
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="terms-checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
        />
        <label htmlFor="terms-checkbox" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
          I agree to the{' '}
          <a href="/terms" target="_blank" className="text-primary hover:underline font-medium">
            Terms of Service
          </a>{' '}
          and have read the{' '}
          <a href="/privacy" target="_blank" className="text-primary hover:underline font-medium">
            Privacy Policy
          </a>
        </label>
      </div>
      
      {!acceptedTerms && (
        <p className="text-sm text-muted-foreground text-center">
          Please accept the terms above before clicking the link in your email
        </p>
      )}
      
      {error && <p className="text-base text-destructive">{error}</p>}
      
      <p className="text-xs text-center text-muted-foreground">
        By creating an account, you also agree to receive important updates about your account security and data.
      </p>
      
      {/* Exit option */}
      <p className="text-center text-sm text-muted-foreground mt-4">
        Changed your mind?{' '}
        <Link to="/" className="text-primary hover:underline">
          I'll finish later
        </Link>
      </p>
    </div>
  );
}

function CreatingStep() {
  return (
    <div className="text-center space-y-6 py-12">
      <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
      <p className="text-lg text-muted-foreground">Creating your account...</p>
    </div>
  );
}

function CompleteStep({ earnEntries }: { earnEntries: number }) {
  return (
    <div className="text-center space-y-6 py-12">
      <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center mx-auto">
        <Check className="w-10 h-10 text-primary" />
      </div>
      <div className="space-y-2">
        <p className="text-2xl font-medium text-foreground">Account created!</p>
        <p className="text-lg text-muted-foreground">
          You earned <span className="font-medium text-primary">{earnEntries} entries</span>
        </p>
      </div>
    </div>
  );
}

function ProfileUnlockStep({ 
  remainingEntries, 
  onStartWizard, 
  onSkip 
}: { 
  remainingEntries: number; 
  onStartWizard: () => void; 
  onSkip: () => void;
}) {
  return (
    <div className="text-center space-y-8 py-8 opacity-0 animate-fade-up">
      {/* Animated gift icon */}
      <div className="relative">
        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto animate-bounce-subtle">
          <Gift className="w-12 h-12 text-primary" />
        </div>
        {/* Entry badge */}
        <div className="absolute -top-2 -right-2 left-1/2 transform translate-x-8 bg-primary text-primary-foreground text-sm font-bold px-3 py-1 rounded-full shadow-lg">
          +{remainingEntries}
        </div>
      </div>
      
      <div className="space-y-3">
        <h1 className="text-3xl lg:text-4xl font-medium text-foreground">
          Unlock +{remainingEntries} more entries!
        </h1>
        <p className="text-lg text-muted-foreground max-w-md mx-auto">
          Answer a few quick questions about yourself to boost your jackpot chances
        </p>
      </div>
      
      {/* Benefits */}
      <div className="flex flex-wrap justify-center gap-3 text-sm">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-muted-foreground">
          <Clock className="w-4 h-4" />
          ~2 minutes
        </span>
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-muted-foreground">
          <Check className="w-4 h-4" />
          Save anytime
        </span>
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-medium">
          <Trophy className="w-4 h-4" />
          +{remainingEntries} entries
        </span>
      </div>
      
      <div className="space-y-3 pt-4">
        <button
          onClick={onStartWizard}
          className="w-full py-4 rounded-xl font-bold text-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
        >
          Let's Go!
          <ArrowRight className="w-5 h-5" />
        </button>
        
        <button
          onClick={onSkip}
          className="w-full py-3 text-muted-foreground text-sm hover:text-foreground transition-colors flex items-center justify-center gap-2"
        >
          <Wallet className="w-4 h-4" />
          Skip for now → Go to Dashboard
        </button>
      </div>
      
      <p className="text-xs text-muted-foreground">
        You can always complete this later from your dashboard
      </p>
    </div>
  );
}
